<script lang="ts">
  import CommunityColors from "app/components/+headless/CommunityColors.svelte";
  import WidgetButton from "app/components/widget/WidgetButton.svelte";
  import { onMount, setContext } from "svelte";
  import { writable } from "svelte/store";
  import { crossfade } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import { DocumentsStore } from "app/stores/documents";
  import { documentAPI } from "app/stores/documents_api";
  import { community, communitySlug, widgetAccentColor } from "app/stores/community";
  import { getStores } from "app/stores";
  import {
    iframeParentIsTabletOrDesktop,
    placeholder,
    homeHeading,
    homeSubHeading,
    isWidgetOpen,
    isWidgetExpanded,
    isTabletOrDesktop,
  } from "app/stores/widget";
  import { convertModernRgbToHex } from "app/utils/colors";
  import { generateColorPalette } from "app/components/ai/utils/colors-generator";
  import { session } from "$stores/auth";
  import { track } from "app/utils/analytics";
  import { WidgetButtonCoordinates, WidgetCoordinates } from "app/components/widget/utils";
  import { waitForDomFlush } from "app/components/widget/utils";
  import { type WidgetData } from "./types";

  export let data;

  const { isChromeExtension } = data;
  const { userPreferences } = getStores();
  const docs = new DocumentsStore(documentAPI);

  let showWidget = true;
  let isWidgetReadyForOpeningAnimation = false;
  let chatRef: HTMLDivElement;
  let buttonRef: HTMLButtonElement;
  let key = "widget-button";

  docs.init(data.communitySlug);
  community.hydrate(data.community, data.permissions);
  setContext("docs", docs);

  const context = writable<WidgetData>({
    userId: "",
    buttonText: "",
    metadata: undefined,
  });

  const [send, receive] = crossfade({ duration: 325, easing: expoOut });

  setContext("widgetData", context);

  onMount(() => {
    // if the widget is in extension mode, open the widget
    if (isChromeExtension) {
      $isWidgetOpen = true;
    }

    parent.postMessage({ type: "OUTVERSE_LOADED" }, "*");
    const root = document.querySelectorAll<HTMLDivElement>(":root")[0];

    // set css variables for accent blobs on mount
    for (const [key, value] of Object.entries(generateColorPalette($widgetAccentColor!))) {
      root.style.setProperty(key, value);
    }

    window?.addEventListener("message", (ev) => {
      if (ev.data.hasOwnProperty("hiddenMode")) {
        showWidget = !ev.data.hiddenMode;
      }
      if (ev.data.userId) {
        $context.userId = ev.data.userId;
        console.log("USER ID", $context.userId);
      } else if (ev.data.hasOwnProperty("authedPayload") && ev.data.hasOwnProperty("signature")) {
        widgetAuth(ev.data.authedPayload, ev.data.signature);
      } else if (ev.data.hasOwnProperty("isTabletOrDesktop")) {
        $iframeParentIsTabletOrDesktop = ev.data.isTabletOrDesktop;
      } else if (ev.data.hasOwnProperty("buttonText")) {
        $context.buttonText = ev.data.buttonText;
      } else if (ev.data.theme) {
        console.log("theme toggled", ev.data.theme);
        userPreferences.toggleTheme();
      } else if (ev.data.hasOwnProperty("isVisible")) {
        $isWidgetOpen = ev.data.isVisible;
      } else if (ev.data.hasOwnProperty("isExpanded")) {
        $isWidgetExpanded = ev.data.isExpanded;
      } else if (ev.data.hasOwnProperty("placeholder")) {
        $placeholder = ev.data.placeholder;
      } else if (ev.data.hasOwnProperty("homeHeading")) {
        $homeHeading = ev.data.homeHeading;
      } else if (ev.data.hasOwnProperty("homeSubHeading")) {
        $homeSubHeading = ev.data.homeSubHeading;
      } else if (ev.data.colors) {
        const root = document.querySelectorAll<HTMLDivElement>(":root")[0];

        // set css variables for accent and text color
        if (root) {
          root.style.setProperty("--widget-accent", ev.data.colors.accent);
          root.style.setProperty("--widget-text", ev.data.colors.text);

          // set css variables for accent blobs
          for (const [key, value] of Object.entries(
            generateColorPalette(convertModernRgbToHex(ev.data.colors.accent))
          )) {
            root.style.setProperty(key, value);
          }
        }
      } else if (ev.data.coord) {
        // use coords sent by parent to check if mouse is inside the widget
        const coords = ev.data.coord;
        let target = document.elementFromPoint(coords.x, coords.y);
        if (target && (chatRef?.contains(target) || buttonRef?.contains(target))) {
          parent.postMessage({ type: "OUTVERSE_HOVER" }, "*");
        } else if ($isTabletOrDesktop) {
          parent.postMessage({ type: "OUTVERSE_UNHOVER" }, "*");
        }
      }
    });
  });

  // send message to parent knowing if the widget is open or closed
  $: if ($isWidgetOpen) {
    parent.postMessage({ type: "OUTVERSE_OPEN" }, "*");

    if ($isTabletOrDesktop) {
      isWidgetReadyForOpeningAnimation = true;
    } else {
      // on mobile, we need to wait for the dom to flush before we can open the widget as we are updating the iframe dimensions
      (async () => {
        await waitForDomFlush(); // replaces tick()
        setTimeout(() => {
          isWidgetReadyForOpeningAnimation = true;
        }, 15);
      })();
    }

    track("widget_opened");
  } else {
    parent.postMessage({ type: "OUTVERSE_CLOSE" }, "*");
    isWidgetReadyForOpeningAnimation = false;
    track("widget_closed");
  }

  async function widgetAuth(authedPayload: string, signature: string) {
    /*
     * Calls widgetAuth, and sets the metadata in context (to be used for trivial display purposes only. Any time the
     * data needs to be used eg. to identify the user, it should only ever be pulled out of the claims on the JWT)
     */
    if (await session.widgetAuth($communitySlug, authedPayload, signature)) {
      if (authedPayload) {
        $context.metadata = JSON.parse(authedPayload);
      }
    }
  }
</script>

{#if showWidget}
  <CommunityColors />

  <div class="absolute inset-0">
    <!-- chat widget -->
    {#if $isWidgetOpen && isWidgetReadyForOpeningAnimation}
      <div
        bind:this={chatRef}
        id="chat-widget"
        in:send={{ key }}
        out:receive={{ key }}
        on:pointerenter={() => parent.postMessage({ type: "OUTVERSE_HOVER" }, "*")}
        on:pointerleave={() => {
          // only send unhover event if the widget is on desktop as the mobile already updates the iframe size so this is not needed
          if ($isTabletOrDesktop) {
            parent.postMessage({ type: "OUTVERSE_UNHOVER" }, "*");
          }
        }}
        style={$iframeParentIsTabletOrDesktop
          ? `bottom: ${WidgetCoordinates.bottom}px; right: ${WidgetCoordinates.right}px; top: ${WidgetCoordinates.top}px; left: ${WidgetCoordinates.left}px;`
          : undefined}
        class="{$iframeParentIsTabletOrDesktop
          ? 'widget-in-out-shadow absolute rounded-2xl border-[0.5px] border-black/10 after:rounded-2xl dark:border-white/10'
          : 'fixed inset-0'} z-10 overflow-hidden overscroll-contain bg-white shadow-widget-light dark:bg-neutral-800 dark:shadow-widget-dark"
      >
        <div class="flex size-full flex-col">
          <slot />
        </div>
      </div>
    {:else if !isChromeExtension}
      <!-- used just for crossfade transition -->
      <div
        class="absolute bottom-[72px] right-10 size-10"
        in:send={{ key }}
        out:receive={{ key }}
      />
    {/if}

    <!-- chat open/close button -->
    {#if !isChromeExtension}
      <div
        class="absolute z-0"
        style="bottom: {WidgetButtonCoordinates.bottom}px; right: {WidgetButtonCoordinates.right}px;"
        on:pointerenter={() => parent.postMessage({ type: "OUTVERSE_HOVER" }, "*")}
        on:pointerleave={() => {
          // only send unhover event if the widget is on desktop as the mobile already updates the iframe size so this is not needed
          if ($isTabletOrDesktop) {
            parent.postMessage({ type: "OUTVERSE_UNHOVER" }, "*");
          }
        }}
      >
        <WidgetButton
          bind:buttonRef
          buttonText={$context.buttonText || data.community.widgetButtonText}
        />
      </div>
    {/if}
  </div>
{/if}

<!-- close widget on Escape -->
<svelte:window
  on:keydown={(e) => {
    if (!$isWidgetOpen) return;
    if (e.key === "Escape") {
      $isWidgetOpen = false;
      e.preventDefault();
    }
  }}
/>

<style lang="postcss">
  /* we want to have a hidden background */
  :global(body) {
    background-color: transparent !important;
  }
</style>
